"use client";
import PropCard from "../prop-card";
import Container from "@/components-shared/container";
import { H3, H4 } from "@/components-shared/headers";
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
  CarouselItem,
} from "@/components-shared/ui/carousel";
import React, { Fragment, useEffect } from "react";
import PropCardSkeleton from "../prop-card-skeleton";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { dir } from "i18next";
import { useLocale, useTranslations } from "next-intl";
import { useGetWeekendOffers } from "@/lib/weekend-offers-hook";
import { useIntersectionObserver } from "usehooks-ts";

const Offers = () => {
  const locale = useLocale();
  const t = useTranslations();
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0 });
  const { isLoggedIn } = useSelector((state: RootState) => state.login);
  const {
    data,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetWeekendOffers();

  useEffect(() => {
    refetch();
  }, [isLoggedIn, refetch]);

  useEffect(() => {
    if (isIntersecting && !isFetchingNextPage) fetchNextPage();
  }, [isIntersecting]);

  return (
    <Container className="mb-10 mt-5 sm:mb-16">
      <div className="flex justify-between items-center max-sm:flex-wrap">
        <div className="space-y-3">
          <H3 className="text-green-main font-semibold">
            {t("offers.offersTitle")}
          </H3>
          <H4 className="text-gray-light max-w-md">
            {t("offers.description")}
          </H4>
        </div>
      </div>

      {error ||
      (!data?.pages.flatMap((p) => p?.properties)?.length && !isLoading) ? (
        <div className="h-[304px] flex justify-center items-center">
          <p className="lg:text-[40px] md:text-3xl text-2xl text-gray-light">
            {t("offers.noData")}
          </p>
        </div>
      ) : (
        <Carousel opts={{ direction: dir(locale), dragFree: true }}>
          <CarouselContent className="py-5 sm:py-10 px-2 sm:px-4">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <CarouselItem key={`card ${i}`} className="max-w-xs">
                    <PropCardSkeleton />
                  </CarouselItem>
                ))
              : data?.pages.map((page) => (
                  <Fragment key={page.page}>
                    {page?.properties?.map((prop: Property, i: number) => (
                      <CarouselItem key={`card ${i}`} className="max-w-xs">
                        <PropCard
                          propData={prop}
                          image={prop?.media?.find((m) => m.isMain)?.url}
                          id={prop.id}
                        />
                      </CarouselItem>
                    ))}
                  </Fragment>
                ))}
            {hasNextPage && (
              <CarouselItem ref={ref} className="max-w-xs h-full">
                <PropCardSkeleton />
              </CarouselItem>
            )}
          </CarouselContent>
          <div className="flex gap-4 sm:gap-8 justify-end">
            <CarouselPrevious className="rtl:rotate-180 " />
            <CarouselNext className="rtl:rotate-180" />
          </div>
        </Carousel>
      )}
    </Container>
  );
};

export default Offers;
